import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { BookOpen, Calendar as CalendarIcon, List, Filter, X, Clock, Users, User } from 'lucide-react';
import { coursesAPI } from '../services/api';

interface Course {
  _id: string;
  title: string;
  description: string;
  shortDescription?: string;
  startDate?: string;
  endDate?: string;
  dates?: Array<{ date: string; startTime: string; endTime: string }>; // Array of all course dates
  category?: string;
  subcategory?: string;
  level?: string;
  price?: number;
  isFree?: boolean;
  language?: string;
  image?: string;
  video?: string;
  tags?: string[];
  instructor: {
    _id: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    photo?: string;
  };
  enrolledCount: number;
  isPublished: boolean;
  duration?: number;
  rating?: number;
  whatStudentsWillLearn?: string[];
  skillsTheyWillGain?: string[];
}

// Helper function to get color based on course level
const getCourseColor = (level: string) => {
  const colors: { [key: string]: string } = {
    beginner: '#10b981', // green
    intermediate: '#3b82f6', // blue
    advanced: '#8b5cf6' // purple"
  };
  return colors[level?.toLowerCase()] || '#6b7280'; // gray default
};

const CourseCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    level: ''
  });

  useEffect(() => {
    loadCourses();
  }, [filters]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      
      // Prepare filters for backend - only send non-empty filters
      // Don't filter by isPublished so we can see all courses (including unpublished ones for instructors)
      const backendFilters: any = {};
      if (filters.category) backendFilters.category = filters.category;
      if (filters.level) backendFilters.level = filters.level;
      
      const response = await coursesAPI.getCourses(backendFilters);
      
      // Ensure we have the data in the correct format
      if (response.data && response.data.success && response.data.data) {
        // Map the courses to ensure all fields are properly formatted
        const formattedCourses = response.data.data.map((course: any) => ({
          ...course,
          startDate: course.startDate ? new Date(course.startDate).toISOString() : undefined,
          endDate: course.endDate ? new Date(course.endDate).toISOString() : undefined,
          // Ensure instructor data is properly formatted
          instructor: course.instructor ? {
            _id: course.instructor._id || course.instructor,
            name: course.instructor.name,
            firstName: course.instructor.firstName,
            lastName: course.instructor.lastName,
            photo: course.instructor.photo
          } : { _id: '', name: 'Unknown' },
          // Ensure default values
          enrolledCount: course.enrolledCount || 0,
          isPublished: course.isPublished !== undefined ? course.isPublished : true,
          level: course.level || 'beginner',
          category: course.category || '',
          subcategory: course.subcategory || '',
          description: course.description || course.shortDescription || '',
          price: course.price || 0,
          isFree: course.isFree !== undefined ? course.isFree : true,
          language: course.language || 'fr',
          duration: course.duration || 0,
          rating: course.rating || 0,
          tags: course.tags || [],
          whatStudentsWillLearn: course.whatStudentsWillLearn || [],
          skillsTheyWillGain: course.skillsTheyWillGain || [],
          dates: course.dates || [] // Include dates array
        }));
        
        console.log('Loaded courses:', formattedCourses.length);
        console.log('Courses with dates:', formattedCourses.filter((c: Course) => c.dates && c.dates.length > 0).length);
        formattedCourses.forEach((course: Course) => {
          console.log(`Course: ${course.title}`);
          console.log(`  - Published: ${course.isPublished}`);
          console.log(`  - Dates array:`, course.dates);
          console.log(`  - Dates length:`, course.dates?.length || 0);
          console.log(`  - StartDate:`, course.startDate);
          console.log(`  - EndDate:`, course.endDate);
          if (course.dates && course.dates.length > 0) {
            console.log(`  ✓ Course has ${course.dates.length} dates:`, course.dates);
          } else if (course.startDate) {
            console.log(`  ✓ Course has startDate:`, course.startDate);
          } else {
            console.log(`  ✗ Course has NO dates or startDate`);
          }
        });
        setCourses(formattedCourses);
      } else {
        console.warn('Unexpected response format:', response.data);
        setCourses([]);
      }
    } catch (error: any) {
      console.error('Error loading courses:', error);
      setCourses([]);
      // You could add a toast notification here if needed
    } finally {
      setLoading(false);
    }
  };

  // Convert courses to calendar events
  const calendarEvents = useMemo(() => {
    const events: any[] = [];
    
    console.log('Generating calendar events from', courses.length, 'courses');
    
    const filteredCourses = courses.filter(course => {
      const hasDates = course.dates && Array.isArray(course.dates) && course.dates.length > 0;
      const hasStartDate = !!course.startDate;
      const isPublished = course.isPublished !== false; // Default to true if undefined
      
      console.log(`Course ${course.title}:`);
      console.log(`  - Published: ${isPublished} (value: ${course.isPublished})`);
      console.log(`  - Has dates array: ${!!course.dates}`);
      console.log(`  - Dates is array: ${Array.isArray(course.dates)}`);
      console.log(`  - Dates length: ${course.dates?.length || 0}`);
      console.log(`  - Dates content:`, course.dates);
      console.log(`  - Has startDate: ${hasStartDate}`);
      console.log(`  - Will be included: ${hasDates || hasStartDate}`);
      
      // Show courses that have dates, even if not published (for preview)
      // But published courses are prioritized
      return (hasDates || hasStartDate);
    });
    
    console.log('Filtered courses:', filteredCourses.length);
    
    filteredCourses.forEach(course => {
      try {
        // If course has dates array, create an event for each date
        if (course.dates && Array.isArray(course.dates) && course.dates.length > 0) {
          console.log(`Processing ${course.dates.length} dates for course: ${course.title}`);
          course.dates.forEach((dateItem: any, index: number) => {
            try {
              console.log(`Processing date ${index + 1}:`, dateItem);
              
              // Validate dateItem structure
              if (!dateItem || !dateItem.date) {
                console.warn('Invalid dateItem structure:', dateItem);
                return;
              }
              
              // Ensure times are in correct format
              const startTime = dateItem.startTime || '09:00';
              const endTime = dateItem.endTime || '17:00';
              
              // Ensure date is in YYYY-MM-DD format
              const dateStr = dateItem.date;
              if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                console.warn('Invalid date format (expected YYYY-MM-DD):', dateStr);
                return;
              }
              
              const startDateTime = new Date(`${dateStr}T${startTime}:00`);
              const endDateTime = new Date(`${dateStr}T${endTime}:00`);
              
              console.log(`Parsed dates - start: ${startDateTime.toISOString()}, end: ${endDateTime.toISOString()}`);
              
              if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
                console.warn('Invalid date/time for course event:', course._id, dateItem);
                return;
              }
              
              const event = {
                id: `${course._id}-${index}`,
                title: `${course.title} (${dateItem.startTime} - ${dateItem.endTime})`,
                start: startDateTime.toISOString(),
                end: endDateTime.toISOString(),
                backgroundColor: getCourseColor(course.level || 'beginner'),
                borderColor: getCourseColor(course.level || 'beginner'),
                textColor: '#ffffff',
                extendedProps: {
                  course: course,
                  startTime: dateItem.startTime,
                  endTime: dateItem.endTime,
                  dateIndex: index
                }
              };
              
              console.log('Created event:', event);
              events.push(event);
            } catch (error) {
              console.error('Error processing course date for calendar:', course._id, dateItem, error);
            }
          });
          } else if (course.startDate) {
            // Fallback to startDate/endDate if dates array is not available
            const startDate = new Date(course.startDate);
            const endDate = course.endDate ? new Date(course.endDate) : new Date(startDate);
            
            if (isNaN(startDate.getTime())) {
              console.warn('Invalid start date for course:', course._id);
              return;
            }
            
            const startTime = startDate.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: false 
            });
            const endTime = endDate.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: false 
            });
            
            events.push({
              id: course._id,
              title: `${course.title} (${startTime} - ${endTime})`,
              start: startDate.toISOString(),
              end: endDate.toISOString(),
              backgroundColor: getCourseColor(course.level || 'beginner'),
              borderColor: getCourseColor(course.level || 'beginner'),
              textColor: '#ffffff',
              extendedProps: {
                course: course,
                startTime: startTime,
                endTime: endTime
              }
            });
          }
        } catch (error) {
          console.error('Error processing course for calendar:', course._id, error);
        }
      });
    
    console.log('Total calendar events created:', events.length);
    return events;
  }, [courses]);

  const handleEventClick = (info: any) => {
    // Prevent default navigation
    info.jsEvent.preventDefault();
    info.jsEvent.stopPropagation();
    
    const course = info.event.extendedProps?.course;
    
    if (course) {
      console.log('Setting course details:', course);
      setSelectedCourse(course);
      setShowDetails(true);
    } else {
      // Fallback: try to find the course from the courses array
      const foundCourse = courses.find(c => c._id === info.event.id);
      if (foundCourse) {
        console.log('Found course from array:', foundCourse);
        setSelectedCourse(foundCourse);
        setShowDetails(true);
      } else {
        console.log('Course not found, navigating to course page');
        navigate(`/courses/${info.event.id}`);
      }
    }
  };

  const getInstructorName = (instructor: Course['instructor']) => {
    if (instructor.firstName && instructor.lastName) {
      return `${instructor.firstName} ${instructor.lastName}`;
    }
    return instructor.name || 'Instructor';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Course Calendar</h1>
          <p className="text-gray-600">View scheduled courses on the calendar</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode(viewMode === 'calendar' ? 'list' : 'calendar')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {viewMode === 'calendar' ? (
              <>
                <List className="w-5 h-5" />
                List View
              </>
            ) : (
              <>
                <CalendarIcon className="w-5 h-5" />
                Calendar View
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <select
            value={filters.level}
            onChange={(e) => setFilters({ ...filters, level: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            <option value="Development">Development</option>
            <option value="Technology">Technology</option>
            <option value="Business">Business</option>
            <option value="Marketing">Marketing</option>
            <option value="Agriculture">Agriculture</option>
            <option value="Others">Others</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      ) : viewMode === 'calendar' ? (
        <>
          {calendarEvents.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <BookOpen className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-yellow-800 mb-1">No scheduled courses</h3>
                  <p className="text-sm text-yellow-700 mb-2">
                    {courses.length === 0 
                      ? "No courses found. Create a course and add dates to see them in the calendar."
                      : courses.filter(c => c.isPublished).length === 0
                      ? "No published courses found. Publish a course to see it in the calendar."
                      : "No courses with scheduled dates. Add dates to your courses to see them in the calendar."}
                  </p>
                  <div className="text-xs text-yellow-600 mt-2">
                    <p>Total courses loaded: {courses.length}</p>
                    <p>Published courses: {courses.filter(c => c.isPublished).length}</p>
                    <p>Courses with dates: {courses.filter((c: Course) => c.dates && c.dates.length > 0).length}</p>
                    <p>Courses with startDate: {courses.filter(c => c.startDate).length}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={calendarEvents}
            eventClick={handleEventClick}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            height="auto"
            eventDisplay="block"
            eventTextColor="#fff"
            dayMaxEvents={3}
            firstDay={1}
            weekends={true}
            nowIndicator={true}
            dayHeaderFormat={{ weekday: 'short' }}
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            eventContent={(eventInfo) => {
              const course = eventInfo.event.extendedProps.course;
              const startTime = eventInfo.event.extendedProps.startTime;
              const endTime = eventInfo.event.extendedProps.endTime;
              
              return (
                <div style={{ padding: '4px 6px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '0.85em', marginBottom: '2px', lineHeight: '1.2' }}>
                    {course?.title || eventInfo.event.title}
                  </div>
                  {(startTime || endTime) && (
                    <div style={{ fontSize: '0.7em', opacity: 0.9, marginBottom: '2px' }}>
                      🕐 {startTime} {endTime && `- ${endTime}`}
                    </div>
                  )}
                  {course?.instructor && (
                    <div style={{ fontSize: '0.65em', opacity: 0.85, marginBottom: '2px' }}>
                      👤 {getInstructorName(course.instructor)}
                    </div>
                  )}
                  <div style={{ fontSize: '0.65em', opacity: 0.8, display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {course?.category && (
                      <span>📁 {course.category}</span>
                    )}
                    {course?.level && (
                      <span>• {course.level}</span>
                    )}
                    {course?.isFree && (
                      <span>• Free</span>
                    )}
                  </div>
                </div>
              );
            }}
            eventMouseEnter={(info) => {
              // Show more details on hover
              const course = info.event.extendedProps?.course;
              if (course) {
                info.el.style.cursor = 'pointer';
                info.el.style.zIndex = '1000';
              }
            }}
            moreLinkClick={(info) => {
              // When clicking "more" link, show details for all courses on that day
              const dayEvents = info.allSegs.map((seg: any) => seg.event);
              if (dayEvents.length > 0) {
                const firstCourse = dayEvents[0].extendedProps?.course;
                if (firstCourse) {
                  setSelectedCourse(firstCourse);
                  setShowDetails(true);
                }
              }
            }}
          />
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {courses.filter(c => c.startDate && c.isPublished).length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No scheduled courses</h3>
              <p className="text-gray-600">There are no courses with scheduled dates</p>
            </div>
          ) : (
            courses
              .filter(c => c.startDate && c.endDate && c.isPublished)
              .map((course) => (
                <div
                  key={course._id}
                  onClick={() => {
                    setSelectedCourse(course);
                    setShowDetails(true);
                  }}
                  className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-4">
                    {course.image && (
                      <img
                        src={course.image}
                        alt={course.title}
                        className="w-32 h-24 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span
                          className="inline-block px-2 py-1 text-xs font-semibold rounded text-white"
                          style={{ backgroundColor: getCourseColor(course.level || 'beginner') }}
                        >
                          {course.level || 'Beginner'}
                        </span>
                        {course.category && (
                          <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                            {course.category}
                            {course.subcategory && ` / ${course.subcategory}`}
                          </span>
                        )}
                        {course.isFree && (
                          <span className="text-xs text-green-600 font-semibold px-2 py-1 bg-green-100 rounded">
                            Free
                          </span>
                        )}
                        {course.rating && course.rating > 0 && (
                          <span className="text-xs text-gray-600">
                            ⭐ {course.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{course.title}</h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {course.description || course.shortDescription || 'No description available'}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="h-4 w-4" />
                          <span>
                            <strong>Start:</strong> {formatDateTime(course.startDate)}
                          </span>
                        </div>
                        {course.endDate && (
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            <span>
                              <strong>End:</strong> {formatDateTime(course.endDate)}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span>
                            <strong>Instructor:</strong> {getInstructorName(course.instructor)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span>
                            <strong>Enrolled:</strong> {course.enrolledCount}
                          </span>
                        </div>
                        {course.duration && (
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>
                              <strong>Duration:</strong> {course.duration} min
                            </span>
                          </div>
                        )}
                        {course.language && (
                          <div>
                            <strong>Language:</strong> {course.language.toUpperCase()}
                          </div>
                        )}
                      </div>
                      {course.tags && course.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {course.tags.slice(0, 5).map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/courses/${course._id}`);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex-shrink-0"
                    >
                      View Course
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {/* Course Details Modal */}
      {showDetails && selectedCourse && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDetails(false);
              setSelectedCourse(null);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Course Details</h2>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedCourse(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              {/* Course Image */}
              {selectedCourse.image && (
                <div className="mb-4">
                  <img
                    src={selectedCourse.image}
                    alt={selectedCourse.title}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {selectedCourse.title || 'Course Title'}
                </h3>
                {selectedCourse.description ? (
                  <p className="text-gray-600 mb-4">{selectedCourse.description}</p>
                ) : selectedCourse.shortDescription ? (
                  <p className="text-gray-600 mb-4">{selectedCourse.shortDescription}</p>
                ) : null}
              </div>

              {/* Course Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {selectedCourse.startDate && (
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-500">Start Date</div>
                      <div className="font-medium">{formatDateTime(selectedCourse.startDate)}</div>
                    </div>
                  </div>
                )}
                {selectedCourse.endDate && (
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-500">End Date</div>
                      <div className="font-medium">{formatDateTime(selectedCourse.endDate)}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-gray-500" />
                  <div>
                    <div className="text-sm text-gray-500">Instructor</div>
                    <div className="font-medium">{getInstructorName(selectedCourse.instructor)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-gray-500" />
                  <div>
                    <div className="text-sm text-gray-500">Enrolled</div>
                    <div className="font-medium">{selectedCourse.enrolledCount} students</div>
                  </div>
                </div>
                {selectedCourse.category && (
                  <div>
                    <div className="text-sm text-gray-500">Category</div>
                    <div className="font-medium">
                      {selectedCourse.category}
                      {selectedCourse.subcategory && (
                        <span className="text-gray-400"> / {selectedCourse.subcategory}</span>
                      )}
                    </div>
                  </div>
                )}
                {selectedCourse.level && (
                  <div>
                    <div className="text-sm text-gray-500">Level</div>
                    <span
                      className="inline-block px-2 py-1 text-xs font-semibold rounded text-white"
                      style={{ backgroundColor: getCourseColor(selectedCourse.level) }}
                    >
                      {selectedCourse.level}
                    </span>
                  </div>
                )}
                {selectedCourse.duration !== undefined && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="text-sm text-gray-500">Duration</div>
                      <div className="font-medium">{selectedCourse.duration} minutes</div>
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-gray-500">Price</div>
                  <div className="font-medium">
                    {selectedCourse.isFree ? (
                      <span className="text-green-600 font-semibold">Free</span>
                    ) : (
                      <span>${selectedCourse.price || 0}</span>
                    )}
                  </div>
                </div>
                {selectedCourse.language && (
                  <div>
                    <div className="text-sm text-gray-500">Language</div>
                    <div className="font-medium">{selectedCourse.language.toUpperCase()}</div>
                  </div>
                )}
                {selectedCourse.rating !== undefined && selectedCourse.rating > 0 && (
                  <div>
                    <div className="text-sm text-gray-500">Rating</div>
                    <div className="font-medium">⭐ {selectedCourse.rating.toFixed(1)}/5</div>
                  </div>
                )}
              </div>

              {/* Tags */}
              {selectedCourse.tags && selectedCourse.tags.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm text-gray-500 mb-2">Tags</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCourse.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* What Students Will Learn */}
              {selectedCourse.whatStudentsWillLearn && selectedCourse.whatStudentsWillLearn.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-700 mb-2">What You'll Learn</div>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    {selectedCourse.whatStudentsWillLearn.slice(0, 5).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Skills They Will Gain */}
              {selectedCourse.skillsTheyWillGain && selectedCourse.skillsTheyWillGain.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Skills You'll Gain</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCourse.skillsTheyWillGain.slice(0, 6).map((skill, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => navigate(`/courses/${selectedCourse._id}`)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  View Full Course
                </button>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedCourse(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseCalendar;

